import type { PropsWithChildren, FunctionComponent } from 'react'
import { useCallback, useState, useMemo } from 'react'
import { colors, NewSelect, Text } from '@retailer-platform/shared-components'
import { SelectField } from '@retailer-platform/shared-components/src/tds'
import { useFetchAislesForDepartmentQuery } from '../../../__codegen__/api'
import { useDashMessage } from '../../../intl/intl.hooks'

interface Props {
  departmentId: string
  isDisabled?: boolean
  onChange: (selectedAisleId: string) => void
  initialValue?: string
}

const AislesSelectWithDepartmentId: FunctionComponent<PropsWithChildren<Props>> = ({
  departmentId,
  onChange,
  isDisabled,
  initialValue,
}) => {
  const [aisleId, setAisleId] = useState<string>(initialValue)

  const { data, loading, error } = useFetchAislesForDepartmentQuery({
    variables: { departmentId },
  })

  const defaultLabelMessage = useDashMessage('components.aislesSelect.defaultLabel')

  const aisles = data?.catalogDepartment?.aisles

  const aislesOptions = useMemo(() => {
    if (!aisles) return []

    const defaultOption = { label: defaultLabelMessage, value: undefined }
    const aisleOptions = aisles.map(aisle => ({ label: aisle.name, value: aisle.id }))

    return [defaultOption, ...aisleOptions]
  }, [aisles, defaultLabelMessage])

  const handleOnChange = useCallback(
    (selectedAisleId?: string) => {
      setAisleId(selectedAisleId)
      onChange(selectedAisleId)
    },
    [onChange]
  )

  if (aislesOptions.length > 0) {
    // Mantine select requires value to be defined
    aislesOptions[0].value = ''
  }

  return (
    <>
      <SelectField
        placeholder={defaultLabelMessage}
        isLoading={loading}
        onChange={handleOnChange}
        options={aislesOptions}
        value={aisleId}
        searchable={true}
        aria-label="aisles-select"
        disabled={isDisabled || !!error}
        data-testid="aisles-select"
      />
      <Text css={{ color: `${colors.DETRIMENTAL.REGULAR}` }}>
        <>{error}</>
      </Text>
    </>
  )
}

export const AislesSelect: FunctionComponent<PropsWithChildren<Props>> = ({
  departmentId,
  ...rest
}) => {
  const placeholder = useDashMessage('components.departmentsSelect.noDepartment')

  /* 
    Using this approach to conditionally call the useAislesSelectGetAislesQuery.
  */
  if (!departmentId) {
    return (
      <>
        <SelectField
          placeholder={placeholder}
          value={null}
          disabled
          onChange={() => null}
          options={[]}
          aria-label="aisles-select-no-department"
          data-testid="aisles-select-no-department"
        />
      </>
    )
  }

  return <AislesSelectWithDepartmentId departmentId={departmentId} {...rest} />
}
